/** @prettier */

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Stack,
} from '@mui/material';
import {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
import TextFieldSelectBox, {
  SLOUAddressSelectBox,
} from '../../component/SelectBox';
import APIErrorMessage from '../../component/APIErrorMessage';
//API import
import {
  patchCompositionsSLUnit,
  getCompositionsSLUnit,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {slInfoSort} from '../../functions/sort';
import {format, pullDownMenu} from '../../define';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';

/**
 * 親機として登録されている数(子機の数)を求める
 * @param parentList 親機として登録されているユニットIDの一覧
 * @param unitID ユニットID
 * @param editUnitParent 編集するユニットの親室外機のID
 * @returns 親機として登録されている数
 */
const asParentUseCount = (
  parentList: (string | undefined)[],
  unitID: string,
  editUnitParent: string | undefined
) => {
  //編集中ユニットの親機は、選択肢に含めるため0を返す
  if (unitID === editUnitParent) return 0;
  //親機として登録されている数(子機の数)を求める
  return parentList.reduce((result, parentID) => {
    if (parentID === unitID) result++;
    return result;
  }, 0);
};

/**
 * 室外機編集ダイアログ
 * @param props props
 * @returns JSX.Element
 */
const OUEditDialog = (props: {
  /** 修正する室外機ID */
  ouID: string;
  /** 修正する室外機の情報 */
  displayOUInfo: apiIF.resParameters.slUnitInformations;
  /** 室内外機の情報（set関数） */
  setSLInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getCompositionsSLUnit | null>
  >;
  /** ダイアログを開くかどうか */
  isOpenDialog: boolean;
  /** ダイアログを開くかどうか（set関数） */
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  /** 課金しているかどうか */
  isBilling: boolean;
  /** GW毎の構成情報未反映の有無（set関数） */
  setHasConfigGW: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  //パスパラメータ取得
  const {siteID, gwID, systemType: slType} = useParams();

  //編集前の室外機情報を取得
  const tempOUID = props.displayOUInfo['outdoor-id'];
  const tempOUName =
    props.displayOUInfo['outdoor-unit-name'] === undefined
      ? ''
      : props.displayOUInfo['outdoor-unit-name'];
  const tempOUSystem =
    props.displayOUInfo['sl-system'] === undefined
      ? ''
      : props.displayOUInfo['sl-system'];
  const tempOUAddress =
    props.displayOUInfo['sl-address'] === undefined
      ? ''
      : props.displayOUInfo['sl-address'];
  const tempOUVoltage =
    props.displayOUInfo['input-voltage'] === undefined
      ? '0'
      : props.displayOUInfo['input-voltage'];
  const tempOUPowerSupply =
    props.displayOUInfo['power-supply'] === undefined
      ? 'threePhase'
      : props.displayOUInfo['power-supply'];
  const tempOUParent =
    props.displayOUInfo['parent-outdoor-unit'] === undefined ||
    props.displayOUInfo['parent-outdoor-unit'] === ''
      ? '0'
      : props.displayOUInfo['parent-outdoor-unit'];
  const tempOUType =
    props.displayOUInfo['outdoor-unit-type'] === undefined
      ? 'KXZ'
      : props.displayOUInfo['outdoor-unit-type'];

  const [ouName, setOUName] = useState(tempOUName); //室外機の名前
  const [slSystem] = useState(tempOUSystem); //系統番号
  const [ouAddress, setOUAddress] = useState(tempOUAddress); //SLアドレス
  const [ouType, setOUType] = useState(tempOUType); //室外機のタイプ
  const [ouParent, setParent] = useState(tempOUParent); //親子室外機設定
  const [ouPowerSupply, setOUPowerSupply] = useState(tempOUPowerSupply); //電源の仕様
  const [ouVoltage, setOUVoltage] = useState(tempOUVoltage); //入力電圧
  const [parentSelectionItem, setParentSelectionItem] = useState<
    {value: string; label: string}[]
  >([]); //親子室外機設定選択項目
  const [okBtnDisabled, setOKBtnDisabled] = useState(false); //OKボタン非表示フラグ
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  //TextBoxのエラー状態
  const [ouNameError, setOUNameError] = useState(false); //室外機の名前
  const [ouVoltageError, setOUVoltageError] = useState(false); //入力電圧

  const trans = useTranslation().t; //翻訳関数

  //入力項目のチェック
  useEffect(() => {
    if (
      ouName === '' ||
      ouNameError ||
      ouAddress === '' ||
      ouVoltageError ||
      apiWait
    ) {
      setOKBtnDisabled(true);
    } else {
      setOKBtnDisabled(false);
    }
  }, [ouName, ouNameError, ouAddress, ouVoltageError, apiWait]);

  //親室外機のリストを作成
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (siteID === undefined || gwID === undefined || slType === undefined) {
      return;
    }

    //室内外機の情報を取得
    getCompositionsSLUnit(siteID, gwID, {'max-result': 400}).then(response => {
      //SLアドレス順にソート
      response['outdoor-unit-informations'].sort((a, b) => {
        return Number(a['sl-address']) - Number(b['sl-address']);
      });

      //親子室外機設定選択項目作成
      let tempParentSelectionItem = [
        {
          value: '0',
          label: trans(
            'OUEditDialog.superlinkUnit.txtField.choices.label.none'
          ),
        },
      ];
      /** 親機として登録されているユニットIDの一覧 */
      const parentList = response['outdoor-unit-informations'].map(
        slInfo => slInfo['parent-outdoor-unit']
      );
      //編集中のユニットに子機が登録されている場合は、選択肢をNoneのみとする
      if (
        parentList.findIndex(parentUnitID => parentUnitID === props.ouID) === -1
      ) {
        for (const ouInfo of response['outdoor-unit-informations']) {
          if (
            ouInfo['sl-system'] !== slType.slice(2) ||
            ouInfo['outdoor-unit-name'] === undefined ||
            ouInfo['outdoor-id'] === tempOUID ||
            ouInfo['parent-outdoor-unit'] !== '' || //子機の場合
            asParentUseCount(parentList, ouInfo['outdoor-id'], tempOUParent) >=
              3 //子が3台以上登録されている場合
          ) {
            continue;
          }
          tempParentSelectionItem.push({
            value: ouInfo['outdoor-id'],
            label: ouInfo['outdoor-unit-name'],
          });
        }
      }
      setParentSelectionItem(tempParentSelectionItem);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.displayOUInfo]);

  /**
   * OKボタン押下時処理
   * @returns
   */
  const editOU = () => {
    //パスパラメータを取得できない場合
    if (
      siteID === undefined ||
      gwID === undefined ||
      props.displayOUInfo === null ||
      (slSystem !== '1' && slSystem !== '2')
    ) {
      return;
    }

    setAPIWait(true);

    //APIの送信データに室内機の情報を追加する
    //送信データ
    const editData: apiIF.reqParameters.patchCompositionsSLUnit = {
      'outdoor-unit-name': ouName,
      'sl-system': slSystem,
      'sl-address': ouAddress,
      'outdoor-unit-type': ouType,
      'power-supply': ouPowerSupply,
      'input-voltage': ouVoltage,
      'parent-outdoor-unit': ouParent === '0' ? '' : ouParent,
      'indoor-unit-informations':
        props.displayOUInfo['indoor-unit-informations'],
    };
    //情報を修正
    patchCompositionsSLUnit(siteID, gwID, props.ouID, editData)
      .then(() => {
        //修正した情報を取得
        return getCompositionsSLUnit(siteID, gwID, {'max-result': 400});
      })
      .then(response => {
        response = slInfoSort(response);
        props.setSLInfo(() => response);
        props.setIsOpenDialog(false);
        props.setHasConfigGW(props.isBilling); //課金している場合のみ表示
      })
      .catch(() => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setAPIWait(false);
      });
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    props.setIsOpenDialog(false);
    setOUName(tempOUName);
    setOUType(tempOUType);
    setOUAddress(tempOUAddress);
    setOUVoltage(tempOUVoltage);
    setOUPowerSupply(tempOUPowerSupply);
    setParent(tempOUParent);
  };

  return (
    <>
      <Dialog
        open={props.isOpenDialog}
        onClose={handleClose}
        aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">
          {trans('OUEditDialog.superlinkUnit.title')}
        </DialogTitle>
        <DialogContent>
          {/* ダイアログ内に表示するメッセージ */}
          <DialogContentText>
            {trans('OUEditDialog.superlinkUnit.msg')}
          </DialogContentText>
          <Stack spacing={1.5}>
            {/* 室外機名称 */}
            <ChkInputTextbox
              setValue={setOUName}
              setError={setOUNameError}
              chkLength={format.BASIC_LENGTH_MAX}
              type="text"
              label={trans(
                'OUEditDialog.superlinkUnit.txtField.label.unitName'
              )}
              value={ouName}
              id="mv-editOUName"
              class="gwSetting__contents__textBox--unitName"
              helperText=" "
              errHelperText={trans(
                'OUEditDialog.superlinkUnit.txtField.errHelperTxt'
              )}
              variant="standard"
              autoFocus={true}
              margin="dense"
              chkInputProhibitedCharacter={format.BASIC_PATTERN_S3_POLICY}
            />
            {/* 室外機アドレス */}
            <SLOUAddressSelectBox
              label={trans(
                'OUEditDialog.superlinkUnit.txtField.label.superlinkAddress'
              )}
              setAddress={setOUAddress}
              default={ouAddress}
              id="mv-selectOuUnit"
              class="gwSetting__contents__textBox--address"
            />
            {/* 電圧 */}
            <ChkInputTextbox
              setValue={setOUVoltage}
              setError={setOUVoltageError}
              chkInputTxtOK={format.VOLTAGE_PATTERN_OK}
              type="text"
              label={trans('OUEditDialog.superlinkUnit.txtField.label.voltage')}
              value={ouVoltage}
              id="mv-editVoltageInput"
              class="gwSetting__contents__textBox--voltage"
              helperText=" "
              errHelperText={trans(
                'OUEditDialog.superlinkUnit.txtField.errHelperTxt'
              )}
              variant="standard"
            />
            {/* 電源の種類 */}
            <TextFieldSelectBox
              label={trans(
                'OUEditDialog.superlinkUnit.txtField.label.powerSupply'
              )}
              choices={pullDownMenu.POWER_SUPPLY_TYPE}
              setValue={setOUPowerSupply}
              default={ouPowerSupply}
              id="mv-editPhaseSelect"
              class="gwSetting__contents__selectBox--powerSupply"
            />
            {/* 親室外機 */}
            {parentSelectionItem.length > 0 ? (
              <TextFieldSelectBox
                label={trans(
                  'OUEditDialog.superlinkUnit.txtField.label.parentUnit'
                )}
                choices={parentSelectionItem}
                setValue={setParent}
                default={ouParent}
                id="mv-editParentOuSelect"
                class="gwSetting__contents__textBox--unitName"
              />
            ) : (
              <TextField
                label={trans(
                  'OUEditDialog.superlinkUnit.txtField.label.parentUnit'
                )}
                variant="standard"
                defaultValue={trans(
                  'OUEditDialog.superlinkUnit.txtField.defaultValue.loadingMsg'
                )}
                disabled
              />
            )}
            {/* 室外機タイプを選択するラジオボタン(1st stepでは非表示とする) */}
            {/* <FormControl>
              <FormLabel>Outdoor unit type</FormLabel>
              <RadioGroup
                row
                defaultValue={ouType}
                aria-label="position"
                name="position">
                <FormControlLabel
                  value="BeforeKXE6"
                  control={
                    <Radio color="primary" id={'mv-editBeforeKXE6btn'} />
                  }
                  label="KXE6 or before"
                  onChange={() => setOUType('BeforeKXE6')}
                />
                <FormControlLabel
                  value="KXZ"
                  control={<Radio color="primary" id={'mv-editKXZbtn'} />}
                  label="KXZ"
                  onChange={() => setOUType('KXZ')}
                />
                <FormControlLabel
                  value="KXZ2"
                  control={<Radio color="primary" id={'mv-editKXZ2btn'} />}
                  label="KXZ2"
                  onChange={() => setOUType('KXZ2')}
                />
              </RadioGroup>
            </FormControl> */}
          </Stack>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            disabled={apiWait}
            onClick={handleClose}
            buttonText={trans('OUEditDialog.buttonTxt.cancel')}
            id={'mv-ouEditCancelBtn'}
          />
          <PrimaryButton
            onClick={editOU}
            buttonText={trans('OUEditDialog.buttonTxt.ok')}
            id={'mv-btnOkEditOu'}
            disabled={okBtnDisabled}
            apiWait={apiWait}
          />
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
        id={`${tempOUName}EditErrDlgBtn`}
      />
    </>
  );
};
export default OUEditDialog;
